<template>
  <div class="stipend-request-summary" redesign>
    <!-- Header -->
    <div class="local-hero">
      <h1 class="local-hero-header">Your Stipend Request</h1>
    </div>

    <div class="form-container stipend-request-summary-form">
      <LoadSpinner v-if="loading" :color="`#${organizationDetails.darkBrandColor}`" />
      <template v-if="!loading">
        <!-- Stipend Request Metadata -->
        <div class="form-field stipend-request-meta-data">
          <div><span><translate>Program Month</translate>:</span> {{ this.currentRequest.programMonth }}</div>
          <div><translate>Amount</translate>: {{ this.currentRequest.amount | currency }}</div>
          <div><translate>Status</translate>: {{ this.currentRequest.status }}</div>
          <div><translate>Submitted On</translate>: {{ this.currentRequest.submittedOn | formatDateShort($language.current) }}</div>
        </div>

        <!-- Messages -->
        <div class="form-field view-messages">
          <a @click="openMessageCenter" v-translate>View messages</a>&nbsp;<div class="unread-messages" @click="openMessageCenter">{{ messageUnreadCount() }}</div>
        </div>

        <!-- Impact Statement -->
        <div class="form-field" v-if="currentRequest.impactStatementResponse">
          <h4 class="form-field-header" v-translate>Impact Statement</h4>
          <div class="form-field-container card-shape">
            <div v-for="question in currentRequest.impactStatementResponse.questions" class="survey-question">
              <div class="survey-question-text field-name">{{ question.text }}</div>
              <div v-if="question.answer" class="survey-question-answer field-value">{{ question.answer.textEn || question.answer.text }}</div>
              <div v-else class="survey-question-answer field-value">No answer submitted.</div>
            </div>
          </div>
        </div>

        <!-- Personal Details -->
        <div class="form-field" v-if="currentRequest.personalDetailsResponse">
          <h4 class="form-field-header" v-translate>Personal Details</h4>
          <div class="form-field-container card-shape">
            <div v-for="question in currentRequest.personalDetailsResponse.questions" class="survey-question">
              <div class="survey-question-text field-name">{{ question.text }}</div>
              <div v-if="question.answer" class="survey-question-answer field-value">{{ question.answer.textEn || question.answer.text }}</div>
              <div v-else class="survey-question-answer field-value">No answer submitted.</div>
            </div>
          </div>
        </div>

        <!-- Goal Progression -->
        <div class="form-field" v-if="currentRequest.goalProgressionResponse">
          <h4 class="form-field-header" v-translate>Goal Progression</h4>
          <div class="form-field-container card-shape">
            <div v-for="question in currentRequest.goalProgressionResponse.questions" class="survey-question">
              <div class="survey-question-text field-name">{{ question.text }}</div>
              <div v-if="question.answer" class="survey-question-answer field-value">{{ question.answer.textEn || question.answer.text }}</div>
              <div v-else class="survey-question-answer field-value">No answer submitted.</div>
            </div>
          </div>
        </div>

        <!-- Program Exit -->
        <div class="form-field" v-if="currentRequest.programExitResponse">
          <h4 class="form-field-header" v-translate>Program Exit</h4>
          <div class="form-field-container card-shape">
            <div v-for="question in currentRequest.goalProgressionResponse.questions" class="survey-question">
              <div class="survey-question-text field-name">{{ question.text }}</div>
              <div v-if="question.answer" class="survey-question-answer field-value">{{ question.answer.textEn || question.answer.text }}</div>
              <div v-else class="survey-question-answer field-value">No answer submitted.</div>
            </div>
          </div>
        </div>

        <!-- Year 2 Application -->
        <div class="form-field" v-if="currentRequest.year2ApplicationResponse">
          <h4 class="form-field-header" v-translate>Year 2 Application</h4>
          <div class="form-field-container card-shape">
            <div v-for="question in currentRequest.year2ApplicationResponse.questions" class="survey-question">
              <div class="survey-question-text field-name">{{ question.text }}</div>
              <div v-if="question.answer" class="survey-question-answer field-value">{{ question.answer.textEn || question.answer.text }}</div>
              <div v-else class="survey-question-answer field-value">No answer submitted.</div>
            </div>
          </div>
        </div>

        <DocumentUpload
          v-if="currentRequest.programMonth !== 13"
          :stipend="currentRequest"
          :documentList.sync="documents"
          :loading="documentsLoading"
          docDetailType="STIPEND"
          @documents-changed="onDocumentsChanged($event, currentRequest.id)"
          @delete-document="onDeleteDocument($event, currentRequest.id)"
          @document-detail-type-selected="updateDocumentsDetailType($event, currentRequest.id)"
          :darkText="true"
        />
      </template>
    </div>

    <!-- Messaging center modal -->
    <Modal
      :additionalModalStyles="{
        width: '100%',
        'max-width': '500px',
        border: 'none',
        padding: 0,
        overflow: 'hidden scroll',
      }"
      :additionalContainerStyles="{
        zIndex: '1001',
        padding: 0,
      }"
      v-if="mode === MESSAGE_MODE"
      :scrollToBottom="true"
      dimmed
    >
      <div class="message-center">
        <div class="message-center-header">
          <span v-translate>Messages</span> <font-awesome-icon class="menu-close" @click="closeMessageCenter" :icon="['fas', 'times']" />
        </div>
        <div class="message-center-thread">
          <MessagesList
            :messages="messages"
            :user="user"
            @next-page-loaded="markAllMessagesRead"
          />
        </div>
        <div class="message-center-form">
          <MessageForm :user="currentRequest.userRef" :stipendRequest="currentRequest" @created="onMessageCreated" :darkText="true" />
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import MessageForm from '@/views/common/MessageForm'
import MessagesList from '@/views/common/MessagesList'
import CollectionManager from '@/services/collectionManager'
import Message from '@/services/messages'
import { LoadSpinner } from '@/components'
import StipendRequest from '@/services/stipendRequests'
import Modal from '@/components/Modal'
import DocumentUpload from '@/components/requests/DocumentUpload'
import Document from '@/services/documents'

const STIPEND_REUQEST_DETAIL_MODE = 'STIPEND_REUQEST_DETAIL'
const MESSAGE_MODE = 'MESSAGE'

export default {
  name: 'StipendRequestSummary',
  components: {
    LoadSpinner,
    MessageForm,
    MessagesList,
    Modal,
    DocumentUpload,
  },
  data() {
    return {
      STIPEND_REUQEST_DETAIL_MODE,
      MESSAGE_MODE,
      mode: STIPEND_REUQEST_DETAIL_MODE,
      messages: this.getMessages(),
      documentsLoading: false,
      loading: true,
      documents: [],
      killList: [],
    }
  },
  async mounted() {
    await this.refresh();
    this.loading = false
  },
  methods: {
    getMessages() {
      const rawMessages = CollectionManager.create({
        ModelClass: Message,
        filters: {
          ordering: 'datetime_created',
          message_type: `${Message.options.DIGITAL_MESSAGE_ENGLISH},${Message.options.DIGITAL_MESSAGE_SPANISH},${Message.options.DIGITAL_MESSAGE}`,
        },
      })
      return rawMessages
    },
    async refresh() {
      //Fetch the most recent data from the API, because why not
      const stipendRequest = await StipendRequest.api.retrieve(this.currentRequest.id)
      this.documents = stipendRequest.documentsRef
      this.$store.dispatch('updateCurrentRequest', stipendRequest)
      await this.refreshMessages()
    },
    async refreshMessages() {
      if (this.currentRequest) {
        this.messages.filters.stipend_request = this.currentRequest.id
        return this.messages.refresh()
      }
    },
    async onDocumentsChanged(documentList) {
      this.documentsLoading = true
      // Replace all documents for this particular event with the newest set
      this.documents = documentList

      if(this.documents && this.documents.length)
        this.error = false

      await this.updateDocuments()
      StipendRequest.api.trackUpdate(this.currentRequest, StipendRequest.options.DOCUMENT_ADDED)
      this.documentsLoading = false
    },
    async onDeleteDocument(docToDelete) {
      this.documentsLoading = true
      // If a document the user wants to delete has an ID, it means it exists in the database.
      // That means we need to keep track of it, and send a command to delete it later.
      if (docToDelete.id) {
        this.killList.push(docToDelete)
      }
      this.documents = this.documents.filter(doc => doc.id !== docToDelete.id)

      await this.updateDocuments()
      StipendRequest.api.trackUpdate(this.currentRequest, StipendRequest.options.DOCUMENT_DELETED)
      this.documentsLoading = false
    },
    async updateDocuments() {
      // Collect documents
      const documentList = this.documents

      // Loop through all documents from each event
      const promiseList = []
      documentList.forEach(doc => {
        const index = documentList.indexOf(doc)
        // If document does not have an ID, it doesn't yet exist in the DB - so we need to create it
        if (!doc.id) {
          promiseList.push(
            Document.api.create(doc).then(document => (documentList[index] = document)),
          )
        }
      })

      // Delete every document the user removed
      this.killList.forEach(doc => {
        promiseList.push(Document.api.delete(doc))
      })

      // Wait for all Documents to be modified before updating Request
      await Promise.all(promiseList)
      this.killList = []

      // Modify the request in state
      this.currentRequest.documentsRef = this.documents
      this.$store.dispatch('updateCurrentRequest', this.currentRequest)
    },
    async updateDocumentsDetailType(docDetailType, stipendRequestId) {
      // Do nothing
    },
    messageUnreadCount() {
        return this.messages.list.filter((message) => message.isUnread).length
    },
    openMessageCenter() {
      this.mode = MESSAGE_MODE
      this.markAllMessagesRead()
    },
    closeMessageCenter() {
      this.mode = STIPEND_REUQEST_DETAIL_MODE
    },
    markAllMessagesRead() {
      this.messages.list
        .filter((m) => m.isUnread)
        .forEach((m) => {
          m.isUnread = false
          Message.api.markRead(m).catch(() => {
            m.isUnread = true
          })
        })
      this.refresh()
    },
    onMessageCreated(message) {
      this.messages.list.push(message)

      // Scroll down
      setTimeout(() => {
        let modalContainer = document.getElementById("modal_container")
        modalContainer.scrollTop = modalContainer.scrollHeight
      }, 250)
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'organizationCssVars', 'currentRequest']),
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/responsive';

.stipend-request-summary {
  display: flex;
  flex-direction: column;

  .stipend-request-summary-form {
    @extend .constrain-tablet-and-up;

    @media (min-width: 1140px) {
      max-width: 1076px !important;
    }
  }

  .stipend-request-meta-data {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    background-color: var(--dark-brand-color);
    padding: 1rem 2rem;
    border-radius: 1rem;

    @include mobile-screen {
      flex-direction: column;

      div {
        margin-bottom: 1rem;
      }
    }

    span {
      font-weight: bold;
    }
  }

  .survey-question {
    margin-bottom: 2.5rem;

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  .view-messages {
    text-align: center;
    font-size: 1.125rem;
    font-weight: bold;
    line-height: 1.5rem;
    vertical-align: center;

    a {
      font-weight: bold !important;
    }

    .unread-messages {
      background-color: #D90F10;
      border-radius: 0.75rem;
      color: white;
      cursor: pointer;
      display: inline-block;
      font-size: 1rem;
      font-weight: normal;
      margin-left: 0.5rem;
      padding: 0 0.4rem;
      position: relative;
      top: -2px;
    }
  }

  .message-center {
    background-color: white;
    display: flex;
    flex-direction: column;
    height: 100vh;
    max-height: 100vh;

    .message-center-header {
      background: white;
      box-shadow: 0 2px 4px 0 rgba(0,0,0,0.1);
      font-size: 1.5rem;
      font-weight: bold;
      line-height: 2rem;
      padding: 1rem 1.5rem;
      position: sticky;
      top: 0px;

      .menu-close {
        cursor: pointer;
        float: right;
      }
    }

    .message-center-thread {
      background-color: white;
      padding: 0 1rem;
    }

    .message-center-form {
      background: white;
      border-top: 1px solid #DFDFE0;
      position: sticky;
      bottom: 0px;
      width: 100%;
      padding: 1rem;
    }
  }
}

</style>
